import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import {
  DemanderKbn,
  Prefectures,
  YesOrNoFlug,
} from "../../assets/data/Constants";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import { GlobalContext } from "../../context/globalContext";
import { BusinessContext } from "../../context/businessContext";
import TkStepper from "../../components/atoms/TkStepper";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import TkBackButton from "../../components/molecules/TkBackButton";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";
import { IAPI_RET005_Request } from "../../type/apiType";
import { IJob_35Props } from "../../type/contextType/businessContext/IJob_35";
import { TkGetCode } from "../../utils/TkGetCode";
import { API_RET005_BuildReqParams } from "../../utils/buildRequestParams";
import { httpAPI_RET005 } from "./api/request.formdata";
import {
  areaBSchema,
  areaCSchema,
  areaDSchema,
  commonSchema,
  fakeTableList,
  heirStepper,
  retireeStepper,
} from "./parts";
import _ from "lodash";

/**
 * SCRR411115 退職手当金の請求手続き（退職所得の申告書の作成２）
 * 退職手当金請求者が退職所得の受給に関する申告書兼退職所得申告書に必要な情報を登録する。
 * @return SCRR411115
 */
const SCRR411115 = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { state, setState } = useContext(GlobalContext);
  const { state: businessState, setState: setBusinessState } =
    useContext(BusinessContext);
  const context = businessState.job_35;
  const isRetiree = context?.demanderKbn === DemanderKbn["退職者本人"];

  const [stateTableList, setStateTableList] = useState<any>([]);
  const [stateSchema, setStateSchema] = useState(commonSchema);

  const [selectedB, setSelectedB] = useState(false);
  const [selectedC, setSelectedC] = useState(false);
  const [selectedD, setSelectedD] = useState(false);
  const stateFakeTableList = useRef(_.cloneDeep(fakeTableList));

  const [areaANo3Date, setAreaANo3Date] = useState({
    start: context?.firstJoinDate!,
    end: "",
  });
  const [currentYearLosDate, setCurrentYearLosDate] = useState({
    start: context?.currentYearLosFrom,
    end: "",
  });
  const [areaBNo5Date, setareaBNo5Date] = useState({ start: "", end: "" });
  const [past4yearsLosDate, setPast4yearsLos] = useState({
    start: context?.past4yearsLosFrom!,
    end: "",
  });
  const [areaCNo7Date, setAreaCNo7Date] = useState({ start: "", end: "" });
  const [areaDNo8Date, setAreaDNo8Date] = useState({
    start: context?.areaDNo8From!,
    end: "",
  });
  const [areaDNo9Date, setAreaDNo9Date] = useState({
    start: context?.areaDNo9From!,
    end: "",
  });
  const [areaDNo10Date, setAreaDNo10Date] = useState({ start: "", end: "" });

  const userType = state?.authInfo?.userType;
  const isUserTypeKiko = userType === authorityUserType.userTypeNumber.KIKO;

  const handleInit = async () => {
    let resArr = stateFakeTableList.current;
    let areaANo3 = resArr.find((item) => item.keyword === "areaANo3")!;

    // 持回り情報を初期セット
    for (let i in businessState.job_35) {
      let targetObj = resArr.find(
        (item) =>
          item.keyword === i &&
          item.keyword !== "currentYearPayerAddress" &&
          item.keyword !== "past4yearsPayerAddress"
      )!;
      let durationStart = resArr.find((item) => `${item.keyword}From` === i)!;
      let durationEnd = resArr.find((item) => `${item.keyword}To` === i)!;

      targetObj &&
        (targetObj.setDefaultValue = context?.[i as keyof typeof context]);

      durationStart &&
        (durationStart.start = {
          wantToSetValue: context?.[i as keyof typeof context],
        });

      durationEnd &&
        (durationEnd.end = {
          wantToSetValue: context?.[i as keyof typeof context],
        });
    }

    context?.firstJoinDate &&
      (areaANo3.start = {
        wantToSetValue: context?.firstJoinDate,
      });
    context?.retireDate &&
      (areaANo3.end = {
        wantToSetValue: context?.retireDate,
      });

    const defaultChecked = (data: any): YesOrNoFlug =>
      !!data ? YesOrNoFlug["有"] : YesOrNoFlug["無"];
    resArr.find((item) => item.keyword === "areaBInputCheck")!.setDefaultValue =
      defaultChecked(!!context?.currentYearLosFrom);
    resArr.find((item) => item.keyword === "areaCInputCheck")!.setDefaultValue =
      defaultChecked(!!context?.past4yearsLosFrom);
    resArr.find((item) => item.keyword === "areaDInputCheck")!.setDefaultValue =
      defaultChecked(!!context?.areaDNo8From);

    let currentYearPayerAddressObj = {
      prefectures: TkGetCode(
        Prefectures,
        context?.currentYearPayerPrefectureCd
      ),
      city: context?.currentYearPayerAddressCity!,
      address: context?.currentYearPayerAddressOthers!,
    };
    resArr.find(
      (item) => item.keyword === "currentYearPayerAddress"
    )!.valueObject = currentYearPayerAddressObj;

    let past4yearsPayerAddressObj = {
      prefectures: TkGetCode(Prefectures, context?.past4yearsPayerPrefectureCd),
      city: context?.past4yearsPayerAddressCity!,
      address: context?.past4yearsPayerAddressOthers!,
    };
    resArr.find(
      (item) => item.keyword === "past4yearsPayerAddress"
    )!.valueObject = past4yearsPayerAddressObj!;

    setSelectedB(!!context?.currentYearLosFrom);
    setSelectedC(!!context?.past4yearsLosFrom);
    setSelectedD(!!context?.areaDNo8From);

    setStateTableList([...resArr]);
  };

  // 初期表示
  useEffect(() => {
    if (state.authInfo?.userType === authorityUserType.userTypeNumber.KIKO) {
      let resArr = stateFakeTableList.current;

      // 退職者名を表示
      let retireeName = resArr.find((item) => item.keyword === "retireeName")!;

      retireeName.showLine = isUserTypeKiko;
      retireeName.contractorNumber = context?.staffNameKanji;

      setStateTableList([...resArr]);
    }

    handleInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let resArr = stateFakeTableList.current;
    let areaBInputCheck = resArr.find(
      (item) => item.keyword === "areaBInputCheck"
    )!;
    let areaCInputCheck = resArr.find(
      (item) => item.keyword === "areaCInputCheck"
    )!;
    let areaDInputCheck = resArr.find(
      (item) => item.keyword === "areaDInputCheck"
    )!;
    let areaANo3 = resArr.find((item) => item.keyword === "areaANo3")!;
    let currentYearLos = resArr.find(
      (item) => item.keyword === "currentYearLos"
    )!;
    let areaBNo5 = resArr.find((item) => item.keyword === "areaBNo5")!;
    let past4yearsLos = resArr.find(
      (item) => item.keyword === "past4yearsLos"
    )!;
    let areaCNo7 = resArr.find((item) => item.keyword === "areaCNo7")!;
    let areaDNo8 = resArr.find((item) => item.keyword === "areaDNo8")!;
    let areaDNo9 = resArr.find((item) => item.keyword === "areaDNo9")!;
    let areaDNo10 = resArr.find((item) => item.keyword === "areaDNo10")!;

    // B欄の表示
    areaBInputCheck.onGetValue = (data: any) => {
      setSelectedB(data === YesOrNoFlug["有"]);
    };

    // C欄の表示
    areaCInputCheck.onGetValue = (data: any) => {
      setSelectedC(data === YesOrNoFlug["有"]);
    };

    // D欄の表示
    areaDInputCheck.onGetValue = (data: any) => {
      setSelectedD(data === YesOrNoFlug["有"]);
    };

    // ③この申告書の提出先から受ける退職手当等の勤続期間
    areaANo3.onGetInputValue = (e: any) => {
      setAreaANo3Date({
        start: e.startDate,
        end: e.endDate,
      });
    };

    // ④本年中に支払いを受けた他の退職手当等についての勤続期間
    currentYearLos.onGetInputValue = (e: any) => {
      setCurrentYearLosDate({
        start: e.startDate,
        end: e.endDate,
      });
    };

    // ⑤　③と④の通算勤続期間
    areaBNo5.onGetInputValue = (e: any) => {
      setareaBNo5Date({
        start: e.startDate,
        end: e.endDate,
      });
    };

    // ⑥前年以前４年以内に支払いを受けた他の退職手当等についての勤続期間
    past4yearsLos.onGetInputValue = (e: any) => {
      setPast4yearsLos({
        start: e.startDate,
        end: e.endDate,
      });
    };

    // ⑦　③又は⑤の勤続期間のうち、⑥の勤続期間と重複している期間
    areaCNo7.onGetInputValue = (e: any) => {
      setAreaCNo7Date({
        start: e.startDate,
        end: e.endDate,
      });
    };

    // ⑧Aの退職手当等についての勤続期間（③）に通算された前の退職手当等についての勤続期間
    areaDNo8.onGetInputValue = (e: any) => {
      setAreaDNo8Date({
        start: e.startDate,
        end: e.endDate,
      });
    };

    // ⑨Bの退職手当等についての勤続期間（④）に通算された前の退職手当等についての勤続期間
    areaDNo9.onGetInputValue = (e: any) => {
      setAreaDNo9Date({
        start: e.startDate,
        end: e.endDate,
      });
    };

    // ⑩　③又は⑤の勤続期間のうち、⑧又は⑨の勤続期間だけからなる部分の期間
    areaDNo10.onGetInputValue = (e: any) => {
      setAreaDNo10Date({
        start: e.startDate,
        end: e.endDate,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    let resArr = stateFakeTableList.current;
    let areaEInputCheck = resArr.find(
      (item) => item.keyword === "areaEInputCheck"
    )!;
    let areaB: any = resArr.filter((_item, index) => index > 7 && index < 10)!;
    let areaC: any = resArr.filter((_item, index) => index > 11 && index < 14)!;
    let areaD: any = resArr.filter((_item, index) => index > 15 && index < 20)!;
    let areaE: any = resArr.filter((_item, index) => index > 21)!;

    // B欄の表示
    areaB.forEach((item: any) => {
      item.showLine = selectedB;
    });

    // C欄の表示
    areaC.forEach((item: any) => {
      item.showLine = selectedC;
    });

    // D欄の表示
    areaD.forEach((item: any) => {
      item.showLine = selectedD;
    });

    // E欄の表示
    areaEInputCheck.setDefaultValue =
      selectedB || selectedC ? YesOrNoFlug["有"] : YesOrNoFlug["無"];
    areaE.forEach((item: any, index: any) => {
      if (index <= 8) {
        item.showLine = selectedB;
      } else {
        item.showLine = selectedC;
      }
    });

    setStateTableList([...resArr]);

    let schema = { ...commonSchema };
    selectedB && Object.assign(schema, areaBSchema);
    selectedC && Object.assign(schema, areaCSchema);
    selectedD && Object.assign(schema, areaDSchema);

    setStateSchema(schema);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedB, selectedC, selectedD]);

  useEffect(() => {
    let resArr = stateFakeTableList.current;
    let areaBNo5 = resArr.find((item) => item.keyword === "areaBNo5")!;
    let areaCNo7 = resArr.find((item) => item.keyword === "areaCNo7")!;
    let areaDNo10 = resArr.find((item) => item.keyword === "areaDNo10")!;
    let areaDNo11 = resArr.find((item) => item.keyword === "areaDNo11")!;

    // 【B】⑤　③と④の通算勤続期間
    areaBNo5.start = {
      wantToSetValue:
        areaANo3Date?.start <= currentYearLosDate?.start!
          ? areaANo3Date?.start
          : currentYearLosDate?.start,
      disabled: true,
    };
    areaBNo5.end = {
      wantToSetValue:
        areaANo3Date?.end >= currentYearLosDate?.end
          ? areaANo3Date?.end
          : currentYearLosDate?.end,
      disabled: true,
    };

    let dateArr: any = [];
    let newDateArr: any = {};

    // 【C】⑦　③又は⑤の勤続期間のうち、⑥の勤続期間と重複している期間
    if (
      past4yearsLosDate?.start <= areaBNo5Date?.end &&
      past4yearsLosDate?.end >= areaANo3Date?.start
    ) {
      dateArr = [
        areaBNo5Date?.start,
        areaBNo5Date?.end,
        past4yearsLosDate?.start,
        past4yearsLosDate?.end,
      ];
    } else if (
      past4yearsLosDate?.start <= areaANo3Date?.end &&
      past4yearsLosDate?.end >= areaANo3Date?.start
    ) {
      dateArr = [
        areaANo3Date?.start,
        areaANo3Date?.end,
        past4yearsLosDate?.start,
        past4yearsLosDate?.end,
      ];
    }

    newDateArr = calcDate(dateArr);
    areaCNo7.start = { wantToSetValue: newDateArr.start, disabled: true };
    areaCNo7.end = { wantToSetValue: newDateArr.end, disabled: true };

    // 【D】⑩　③又は⑤の勤続期間のうち、⑧又は⑨の勤続期間だけからなる部分の期間
    if (
      areaDNo9Date?.start <= areaBNo5Date?.end &&
      areaDNo9Date?.end >= areaBNo5Date?.start
    ) {
      dateArr = [
        areaBNo5Date?.start,
        areaBNo5Date?.end,
        areaDNo9Date?.start,
        areaDNo9Date?.end,
      ];
    } else if (
      areaDNo8Date?.start <= areaANo3Date?.end &&
      areaDNo8Date?.end >= areaANo3Date?.start!
    ) {
      dateArr = [
        areaANo3Date?.start,
        areaANo3Date?.end,
        areaDNo8Date?.start,
        areaDNo8Date?.end,
      ];
    }

    newDateArr = calcDate(dateArr);
    areaDNo10.start = { wantToSetValue: newDateArr.start, disabled: true };
    areaDNo10.end = { wantToSetValue: newDateArr.end, disabled: true };

    // 【D】⑪　⑦と⑩の通算期間
    areaDNo11.start = {
      wantToSetValue:
        areaDNo10Date?.start <= areaCNo7Date?.start
          ? areaDNo10Date?.start
          : areaCNo7Date?.start,
      disabled: true,
    };
    areaDNo11.end = {
      wantToSetValue:
        areaDNo10Date?.end >= areaCNo7Date?.end
          ? areaDNo10Date?.end
          : areaCNo7Date?.end,
      disabled: true,
    };

    setStateTableList([...resArr]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    areaANo3Date,
    currentYearLosDate,
    areaBNo5Date,
    past4yearsLosDate,
    areaCNo7Date,
    areaDNo8Date,
    areaDNo9Date,
    areaDNo10Date,
  ]);

  // 重複期間計算
  const calcDate = (dateArr: any) => {
    let newDateArr = dateArr.sort((a: any, b: any) => a?.localeCompare(b));
    return { start: newDateArr[1], end: newDateArr[2] };
  };

  const makeHttpObj = (obj: any): IAPI_RET005_Request => {
    const reqObj: IAPI_RET005_Request = {
      ...context,
      fileList: context?.fileList ?? [],
      tempSaveStep: isRetiree ? 9 : 10,
      businessType: businessState.common?.businessType,
      contractorNo: isUserTypeKiko
        ? businessState.common?.contractorNo
        : state.authInfo?.contractorNo, // 共済契約者番号
      nowZipcode: isRetiree
        ? context?.demanderZipcode
        : context?.retirementZipcode,
      nowPrefectureCd: isRetiree
        ? context?.demanderPrefectureCd
        : context?.retirementPrefectureCd,
      nowAddressCity: isRetiree
        ? context?.demanderCity
        : context?.retirementAddressCity,
      nowAddressOthers: isRetiree
        ? context?.demanderAddressOthers
        : context?.retirementAddressOthers,
      nRetireKbn: obj.retireKbn, // 退職手当等の退職の区分
      firstJoinDate: obj.areaANo3?.startDate, // 退職手当等についての勤続期間（自）
      retireDate: obj.areaANo3?.endDate, // 退職手当等についての勤続期間（至）
      livingAssistanceFlg: obj.livingAssistanceFlg, // 生活扶助の有無
      paymentConfirmDate: obj.paymentConfirmDate, // 退職手当等の支払いを受けることになった年月日

      // B
      pastLosInCurrentYearTotalFrom:
        obj.areaBInputCheck === YesOrNoFlug["有"]
          ? obj.areaBNo5?.startDate
          : null, // 本年度の退職手当通算勤続期間に通算された前の退職手当勤続期間（自）
      pastLosInCurrentYearTotalTo:
        obj.areaBInputCheck === YesOrNoFlug["有"]
          ? obj.areaBNo5?.endDate
          : null, // 本年度の退職手当通算勤続期間に通算された前の退職手当勤続期間（至）
      currentYearLosFrom:
        obj.areaBInputCheck === YesOrNoFlug["有"]
          ? obj.currentYearLos?.startDate
          : null, // 本年中に支払を受けた他の退職手当等についての勤続期間（自）
      currentYearLosTo:
        obj.areaBInputCheck === YesOrNoFlug["有"]
          ? obj.currentYearLos?.endDate
          : null, // 本年中に支払を受けた他の退職手当等についての勤続期間（至）

      // C
      past4yearsLosFrom:
        obj.areaCInputCheck === YesOrNoFlug["有"]
          ? obj.past4yearsLos?.startDate
          : null, //前年以前4年内（その年に確定拠出年金法に基づく老齢給付金として支給される一時金の支払いを受ける場合には、14年内）の退職手当等についての勤続期間（自）
      past4yearsLosTo:
        obj.areaCInputCheck === YesOrNoFlug["有"]
          ? obj.past4yearsLos?.endDate
          : null, // 前年以前4年内（その年に確定拠出年金法に基づく老齢給付金として支給される一時金の支払いを受ける場合には、14年内）の退職手当等についての勤続期間（至）

      // E
      currentYearPaymentDecisionDate:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.currentYearPaymentDecisionDate
          : null, // 本年中に支払を受けた他の退職手当等の支払を受けることとなった年月日
      currentYearRetirementAllowanceAmt:
        obj.areaEInputCheck === YesOrNoFlug["有"] ? obj.currentYearRetir : null, // 本年中に支払を受けた他の退職手当等の収入金額
      currentYearWithholdingAmt:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.currentYearWithholdingAmt
          : null, // 本年中に支払を受けた他の退職手当等の源泉徴収額
      currentYearScCityAmt:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.currentYearScCityAmt
          : null, // 本年中に支払を受けた他の退職手当等の特別徴収税額（市町村民税）
      currentYearScPrefectureAmt:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.currentYearScPrefectureAmt
          : null, // 本年中に支払を受けた他の退職手当等特別徴収税額（道府県民税）
      currentYearGetPaidDate:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.currentYearGetPaidDate
          : null, // 本年中に支払を受けた他の退職手当等の支払いを受けた年月日
      currentYearRetirementKbn:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.currentYearRetirementKbn
          : null, // 本年中に支払を受けた他の退職手当等の退職の区分
      currentYearPayerPrefectureCd:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? Prefectures[
              obj.currentYearPayerAddress
                ?.prefectures as keyof typeof Prefectures
            ]
          : undefined, // 本年中に支払を受けた他の退職手当等の支払者の住所(都道府県)
      currentYearPayerAddressCity:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.currentYearPayerAddress?.city
          : undefined, // 本年中に支払を受けた他の退職手当等の支払者の住所(市区町村)
      currentYearPayerAddressOthers:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.currentYearPayerAddress?.address
          : undefined, // 本年中に支払を受けた他の退職手当等の支払者の住所(その他)
      currentYearPayerName:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.currentYearPayerName
          : null, // 本年中に支払を受けた他の退職手当等の支払者の名称(氏名)
      past4yearsPaymentDecisionDate:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.past4yearsPaymentDecisionDate
          : null, // 前年以前4年内の退職手当等の支払を受けることとなった年月日
      past4yearsRetirementAllowanceAmt:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.past4yearsRetirementAllowanceAmt
          : null, // 前年以前4年内の退職手当等の収入金額
      past4yearsWithholdingAmt:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.past4yearsWithholdingAmt
          : null, // 前年以前4年内の退職手当等の源泉徴収額
      past4yearsScCityAmt:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.past4yearsScCityAmt
          : null, // 前年以前4年内の退職手当等の特別徴収税額（市町村民税）
      past4yearsScPrefectureAmt:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.past4yearsScPrefectureAmt
          : null, // 前年以前4年内の退職手当等の特別徴収税額（道府県民税）
      past4yearsGetPaidDate:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.past4yearsGetPaidDate
          : null, // 前年以前4年内の退職手当等の支払いを受けた年月日
      past4yearsRetirementKbn:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.past4yearsRetirementKbn
          : null, // 前年以前4年内の退職手当等の退職の区分
      past4yearsPayerZipcode:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? context?.past4yearsPayerZipcode
          : undefined, // 前年以前4年内の退職手当等の支払者の住所(郵便番号)
      past4yearsPayerPrefectureCd:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? Prefectures[
              obj.past4yearsPayerAddress
                ?.prefectures as keyof typeof Prefectures
            ]
          : undefined, // 前年以前4年内の退職手当等の支払者の住所(都道府県)
      past4yearsPayerAddressCity:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.past4yearsPayerAddress?.city
          : null, // 前年以前4年内の退職手当等の支払者の住所(市区町村)
      past4yearsPayerAddressOthers:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.past4yearsPayerAddress?.address
          : null, // 前年以前4年内の退職手当等の支払者の住所(その他)
      past4yearsPayerName:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.past4yearsPayerName
          : null, // 前年以前4年内の退職手当等の支払者の名称(氏名)
      screenId: "SCRR411115",
    };

    return API_RET005_BuildReqParams(reqObj);
  };

  const makeContextObj = (obj: any): IJob_35Props => {
    const contextObj: IJob_35Props = {
      ...context,
      tempSaveStep: isRetiree ? 9 : 10,
      paymentConfirmDate: obj.paymentConfirmDate,
      retireKbn: obj.retireKbn,
      livingAssistanceFlg: obj.livingAssistanceFlg,
      firstJoinDate: obj.areaANo3?.startDate,
      retireDate: obj.areaANo3?.endDate,

      // B
      areaBInputCheck: obj.areaBInputCheck,
      currentYearLosFrom:
        obj.areaBInputCheck === YesOrNoFlug["有"]
          ? obj.currentYearLos?.startDate
          : null,
      currentYearLosTo:
        obj.areaBInputCheck === YesOrNoFlug["有"]
          ? obj.currentYearLos?.endDate
          : null,
      areaBNo5From:
        obj.areaBInputCheck === YesOrNoFlug["有"]
          ? obj.areaBNo5?.startDate
          : null,
      areaBNo5To:
        obj.areaBInputCheck === YesOrNoFlug["有"]
          ? obj.areaBNo5?.endDate
          : null,
      areaBNo5Years:
        obj.areaBInputCheck === YesOrNoFlug["有"] ? obj.areaBNo5?.years : null,

      // C
      areaCInputCheck: obj.areaCInputCheck,
      areaCNo7From:
        obj.areaCInputCheck === YesOrNoFlug["有"]
          ? obj.areaCNo7?.startDate
          : null,
      areaCNo7To:
        obj.areaCInputCheck === YesOrNoFlug["有"]
          ? obj.areaCNo7?.endDate
          : null,
      past4yearsLosFrom:
        obj.areaCInputCheck === YesOrNoFlug["有"]
          ? obj.past4yearsLos?.startDate
          : null,
      past4yearsLosTo:
        obj.areaCInputCheck === YesOrNoFlug["有"]
          ? obj.past4yearsLos?.endDate
          : null,

      // D
      areaDInputCheck: obj.areaDInputCheck,
      areaDNo8From:
        obj.areaDInputCheck === YesOrNoFlug["有"]
          ? obj.areaDNo8?.startDate
          : null,
      areaDNo8To:
        obj.areaDInputCheck === YesOrNoFlug["有"]
          ? obj.areaDNo8?.endDate
          : null,
      areaDNo9From:
        obj.areaDInputCheck === YesOrNoFlug["有"]
          ? obj.areaDNo9?.startDate
          : null,
      areaDNo9To:
        obj.areaDInputCheck === YesOrNoFlug["有"]
          ? obj.areaDNo9?.endDate
          : null,
      areaDNo10From:
        obj.areaDInputCheck === YesOrNoFlug["有"]
          ? obj.areaDNo10?.startDate
          : null,
      areaDNo10To:
        obj.areaDInputCheck === YesOrNoFlug["有"]
          ? obj.areaDNo10?.endDate
          : null,
      areaDNo11From:
        obj.areaDInputCheck === YesOrNoFlug["有"]
          ? obj.areaDNo11?.startDate
          : null,
      areaDNo11To:
        obj.areaDInputCheck === YesOrNoFlug["有"]
          ? obj.areaDNo11?.endDate
          : null,

      // E
      areaEInputCheck: obj.areaEInputCheck,
      currentYearPaymentDecisionDate:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.currentYearPaymentDecisionDate
          : null,
      currentYearRetirementAllowanceAmt:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.currentYearRetirementAllowanceAmt
          : null,
      currentYearWithholdingAmt:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.currentYearWithholdingAmt
          : null,
      currentYearScCityAmt:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.currentYearScCityAmt
          : null,
      currentYearScPrefectureAmt:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.currentYearScPrefectureAmt
          : null,
      currentYearGetPaidDate:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.currentYearGetPaidDate
          : null,
      currentYearRetirementKbn:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.currentYearRetirementKbn
          : null,
      currentYearPayerPrefectureCd:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? Prefectures[
              obj.currentYearPayerAddress
                ?.prefectures as keyof typeof Prefectures
            ]
          : undefined,
      currentYearPayerAddressCity:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.currentYearPayerAddress?.city
          : null,
      currentYearPayerAddressOthers:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.currentYearPayerAddress?.address
          : null,
      currentYearPayerName:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.currentYearPayerName
          : null,
      past4yearsPaymentDecisionDate:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.past4yearsPaymentDecisionDate
          : null,
      past4yearsRetirementAllowanceAmt:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.past4yearsRetirementAllowanceAmt
          : null,
      past4yearsWithholdingAmt:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.past4yearsWithholdingAmt
          : null,
      past4yearsScCityAmt:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.past4yearsScCityAmt
          : null,
      past4yearsScPrefectureAmt:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.past4yearsScPrefectureAmt
          : null,
      past4yearsGetPaidDate:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.past4yearsGetPaidDate
          : null,
      past4yearsRetirementKbn:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.past4yearsRetirementKbn
          : null,
      past4yearsPayerPrefectureCd:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? Prefectures[
              obj.past4yearsPayerAddress
                ?.prefectures as keyof typeof Prefectures
            ]
          : undefined,
      past4yearsPayerAddressCity:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.past4yearsPayerAddress?.city
          : null,
      past4yearsPayerAddressOthers:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.past4yearsPayerAddress?.address
          : null,
      past4yearsPayerName:
        obj.areaEInputCheck === YesOrNoFlug["有"]
          ? obj.past4yearsPayerName
          : null,
    };

    return contextObj;
  };

  const handleSubmit = async (data: any, index: any) => {
    switch (index) {
      case 0: // 一時保存
        // 退職手当金請求更新API実行
        try {
          const res = await httpAPI_RET005({
            ...makeHttpObj(data),
          });

          if (res.resultCd === "I0000") {
            setBusinessState({
              common: businessState.common,
              job_35: {
                ...makeContextObj(data),
                applyId: res.applyId,
              },
            });
            setState({
              ...state,
              errorMsgId: "",
            });
            setOpen(true);
          } else {
            setState({
              ...state,
              errorMsgId: res.resultCd,
            });
          }
        } catch (error) {
          navigate("/error");
        }
        break;

      case 1: // 次へ
        setBusinessState({
          common: businessState.common,
          job_35: { ...makeContextObj(data), tempSaveStep: undefined },
        });
        navigate("/SCRR410016");
        break;
    }
  };

  return (
    <Box data-testid="SCRR411115test">
      <TkStepper
        steps={isRetiree ? retireeStepper : heirStepper}
        activeStep={isRetiree ? 8 : 9}
        savaStep={context?.tempSaveStep}
      />

      <TkFakeTable
        list={stateTableList}
        ruleSchema={stateSchema}
        submitButtonGroup={[
          { submitButtonText: "一時保存" },
          { submitButtonText: "次へ" },
        ]}
        buttonGroup={<TkBackButton />}
        onCheckAndSubmit={(e, index) => handleSubmit(e, index)}
      />

      <TkMessageDialog
        open={open}
        onOky={() => setOpen(false)}
        title="一時保存完了"
        contextText="　一時保存が完了しました。"
        okText="はい"
        cancelText=""
      />
    </Box>
  );
};

export default SCRR411115;
